import {
  AccountResponseV1,
  CreateGroupRequestV1,
  DeleteMode,
  GroupFormModel,
  GroupMemberResponseV1,
  GroupResponseV1,
  GroupType,
  GroupWithPaymentAccount,
  HouseholdRelationship,
  RoleResponseV1,
  UserResponseV1
} from '@access-management/access-management';
import { DeleteEntityDialog } from '@access-management/delete-entity-dialog/delete-entity-dialog';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { refusalReason } from '@core/models/apis';
import { DialogMode } from '@core/models/models';
import { AppScope, scopesAllow } from '@core/models/scope';
import { AccessManagementService } from '@core/services/access-management';
import { InvitationService } from '@core/services/invitation';

interface UserWithRole {
  user: UserResponseV1;
  role: RoleResponseV1;
  groupMemberId: string;
  householdRelationship: HouseholdRelationship;
  isHouseholdAdmin: boolean;
  canChangeRole: boolean;
}

/**
 * The seeded security role whose bundle is ordering, meal planning and the household wallet. Named here
 * because moving onto or off it is the one role change that announces itself to the member, so the
 * confirmation says what that move carries rather than describing every role the same way.
 */
const AdminRoleName = 'Admin';

/** The seeded role that confers nothing, and the other half of what a member can be moved onto. */
const MemberRoleName = 'Member';

@Component({
  selector: 'app-groups-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    IconField,
    InputIcon,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    SelectModule,
    FormField,
    DeleteEntityDialog
  ],
  templateUrl: './groups-table.html',
  styleUrl: './groups-table.scss'
})
export class GroupsTableComponent {
  private readonly accessManagementService = inject(AccessManagementService);
  private readonly invitationService = inject(InvitationService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly groupsResource = this.accessManagementService.createGroupsResource();
  readonly accountsResource = this.accessManagementService.createAccountsResource();
  readonly usersResource = this.accessManagementService.createUsersResource();
  readonly rolesResource = this.accessManagementService.createRolesResource();
  readonly searchTerm = signal<string>('');
  readonly showDialog = signal<boolean>(false);
  readonly dialogMode = signal<DialogMode>(DialogMode.Create);
  readonly selectedGroupId = signal<string | null>(null);
  readonly deletingId = signal<string | null>(null);
  readonly showDeleteDialog = signal<boolean>(false);
  readonly householdPendingDelete = signal<GroupResponseV1 | null>(null);
  readonly saving = signal<boolean>(false);
  readonly showMembersDialog = signal<boolean>(false);
  readonly selectedGroupForMembers = signal<GroupResponseV1 | null>(null);
  readonly membersLoading = signal<boolean>(false);
  readonly groupMembers = signal<GroupMemberResponseV1[]>([]);
  readonly inviteEmail = signal<string>('');
  readonly inviteHouseholdRelationship = signal<HouseholdRelationship>(HouseholdRelationship.Member);
  readonly inviteRoleName = signal<string>('Member');
  readonly inviteInviterUserId = signal<string>('');
  readonly inviting = signal<boolean>(false);

  /**
   * Fills the "Sent by" default once there is an option to fill it with.
   *
   * The roster and the user list are independent resources. Defaulting the moment the roster
   * resolved read inviterOptions while the users were still loading -- it drops every member whose
   * user record is missing -- so on a cold load the default landed on '', the select filled a moment
   * later with nothing selected, and Stage invitation stayed disabled until the operator noticed.
   *
   * Fills an empty selection and replaces one the current options do not contain, so an operator's own
   * choice is left alone while a stale one cannot survive. Filling only an empty selection was not
   * enough: opening a second group clears the selection while the previous group's roster is still in
   * the signal, so this effect re-ran against it and chose that household's owner, and then declined to
   * correct itself once the real roster landed because the selection was no longer empty. The select
   * rendered its placeholder, Stage invitation stayed enabled, and staging posted an inviter from
   * another household.
   */
  private readonly defaultInviter = effect(() => {
    const options = this.inviterOptions();
    const first = options[0]?.value;
    if (!first) return;

    const selected = this.inviteInviterUserId();
    if (!selected || !options.some((option) => option.value === selected)) {
      this.inviteInviterUserId.set(first);
    }
  });
  readonly changingRoleUserId = signal<string | null>(null);

  /**
   * The role each member is staged to move onto, keyed by user id, empty until an operator picks one.
   *
   * Staged rather than applied on selection, so choosing a role and choosing to make the change are two
   * acts. A picker that fired on change has no cancel: dismissing the confirmation leaves the select
   * showing a role the member does not hold, and Angular will not push the old value back through a
   * one-way binding that never changed.
   */
  readonly stagedRoleName = signal<Record<string, string>>({});

  // Household is the only type a create may name. Self was retired first; Event followed, because
  // nothing in the platform creates or consumes one and no roster, roster event, entitlement or token
  // path accounts for it. The enum still carries both so a group that IS one renders correctly.
  readonly groupTypeOptions = [{ label: 'Household', value: GroupType.Household }];

  // Owner is absent deliberately: a household's owner is established when the group is created, not handed
  // out by invitation. Guardian is absent because it is not a relationship at all any more: guardianship
  // is a guardianship assignment over an existing dependent plus a delegated grant.
  //
  // Dependent is absent for a different reason: Access Management refuses one without all four consent
  // timestamps, and a guardian gives those, not support staff. Offering it here produced a refusal that
  // could not be cleared from this screen. It needs the consumer app's dependent flow, not a picker entry.
  readonly inviteHouseholdRelationshipOptions = [{ label: 'Member', value: HouseholdRelationship.Member }];

  readonly groupFormModel = signal<GroupFormModel>({
    paymentAccountId: '',
    name: '',
    description: '',
    groupType: GroupType.Household,
    isActive: true
  });

  readonly groupForm = form(this.groupFormModel, (schemaPath) => {
    required(schemaPath.paymentAccountId, { message: 'Payment account is required' });
    required(schemaPath.name, { message: 'Group name is required' });
  });

  readonly columns = signal<{ field: string; header: string; sortable: boolean }[]>([
    { field: 'name', header: 'Group Name', sortable: true },
    { field: 'paymentAccountName', header: 'Payment Account', sortable: true },
    { field: 'groupType', header: 'Type', sortable: true },
    { field: 'accountCount', header: 'Accounts', sortable: true },
    { field: 'memberCount', header: 'Members', sortable: true },
    { field: 'isActive', header: 'Status', sortable: true },
    { field: 'createdOn', header: 'Created', sortable: true },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  /**
   * Edit mode renames and nothing else. Access Management exposes no endpoint that changes a group's
   * description, type, payment account, or active flag, so the dialog says what it can actually do.
   */
  readonly dialogTitle = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create Group' : 'Rename Group'));

  readonly dialogButtonLabel = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create' : 'Rename'));

  protected readonly isCreateMode = computed(() => this.dialogMode() === DialogMode.Create);

  readonly isLoading = computed(
    () =>
      this.groupsResource.isLoading() ||
      this.accountsResource.isLoading() ||
      this.usersResource.isLoading() ||
      this.rolesResource.isLoading()
  );

  private readonly groups = computed(() => this.groupsResource.value() ?? []);
  readonly accounts = computed(() => this.accountsResource.value() ?? []);
  private readonly users = computed(() => this.usersResource.value() ?? []);
  private readonly roles = computed(() => this.rolesResource.value() ?? []);

  readonly groupsDisplay = computed<GroupWithPaymentAccount[]>(() => {
    const groups = this.groups();
    const accounts = this.accounts();
    const users = this.users();

    return groups.map((group) => {
      const account = accounts.find((a) => a.accountId === group.paymentAccountId);
      const user = account ? users.find((u) => u.userId === account.userId) : undefined;

      return {
        ...group,
        paymentAccountName: account?.name || 'Unknown',
        paymentAccountEmail: user?.email || '',
        accountCount: group.accountIds?.length || 0
      };
    });
  });

  readonly filteredGroups = computed<GroupWithPaymentAccount[]>(() => {
    const searchTerm = this.searchTerm().toLowerCase();
    const groups = this.groupsDisplay();
    if (!searchTerm) return groups;

    return groups.filter(
      (group) =>
        group.name.toLowerCase().includes(searchTerm) ||
        group.paymentAccountName?.toLowerCase().includes(searchTerm) ||
        group.description?.toLowerCase().includes(searchTerm)
    );
  });

  readonly membersWithDetails = computed<UserWithRole[]>(() => {
    const members = this.groupMembers();
    const users = this.users();
    const roles = this.roles();
    // Only a household has roles to move anyone between, and the owner's role is immutable, so the
    // group's type decides whether the action exists at all and the household relationship decides who it reaches.
    const isHousehold = this.selectedGroupForMembers()?.groupType === GroupType.Household;

    return members
      .map((member) => {
        // Falls back to the name the roster itself carries, because the role list is the ACTIVE roles.
        // Deleting a role soft-archives it with no holder check, so a member left on one resolved to
        // nothing here and the filter below dropped their whole row: gone from Current Members while the
        // heading still counted them, with no picker and no remove button to get them back.
        const role: RoleResponseV1 = roles.find((r) => r.roleId === member.roleId) ?? {
          roleId: member.roleId,
          name: member.roleName ?? 'Unknown role',
          isDefault: false,
          isActive: false,
          scopes: [],
          createdOn: new Date(0),
          modifiedOn: new Date(0)
        };

        return {
          user: users.find((u) => u.userId === member.userId)!,
          role,
          groupMemberId: member.groupMemberId,
          householdRelationship: member.householdRelationship,
          isHouseholdAdmin: role.name === AdminRoleName,
          canChangeRole: isHousehold && member.householdRelationship !== HouseholdRelationship.Owner
        };
      })
      .filter((m) => m.user);
  });

  /**
   * The two roles a member can be put on. Owner is absent because a household's owner is established
   * when the group is created and the Owner bundle is the household outright: handing it out would give
   * someone every manage scope, including the one that changes what everyone else may do.
   *
   * Anything else is absent because role authoring is retired. A role left over from when it existed is
   * still returned by the roles endpoint until it is archived, and offering one produced a refusal the
   * screen could not explain. The engine applies the same rule on both the invite and the assign path,
   * so this is the screen agreeing with it rather than being it.
   */
  private readonly assignableRoles = computed<RoleResponseV1[]>(() => {
    const assignable = [MemberRoleName, AdminRoleName];
    return assignable
      .map((name) => this.roles().find((role) => role.name?.trim().toLowerCase() === name.toLowerCase()))
      .filter((role): role is RoleResponseV1 => role !== undefined);
  });

  /** The role an existing member can be moved onto, offered per row in the roster. */
  readonly assignableRoleOptions = computed<{ label: string; value: string }[]>(() =>
    this.assignableRoles().map((role) => ({ label: role.name, value: role.name }))
  );

  /**
   * The role the invitee holds once they accept. The same list, because the engine applies the same rule
   * to both paths: named separately so the two call sites read as the two questions they are.
   */
  readonly inviteRoleOptions = this.assignableRoleOptions;

  /**
   * Who the invitation can be sent as: whoever effectively holds `Members.Invite` in the target group.
   * Access Management refuses any other inviter, so offering them would produce a 403 that reads as a bug
   * in this screen. Staff reach the endpoint on their own admin grant, but the invitation is still
   * recorded as that member's.
   *
   * It reads the member's effective scopes rather than their role name, so a household that has granted
   * the capability to someone other than the owner gets that person in the list, and an owner whose
   * bundle does not carry it is not offered for an invitation the engine would refuse. A deny cannot
   * produce that second case: the engine hands an owner their bundle untouched, so the reachable way
   * there is a bundle that never carried the scope.
   */
  readonly inviterOptions = computed<{ label: string; value: string }[]>(() => {
    const users = this.users();

    return this.groupMembers()
      .filter((member) => scopesAllow(member.scopes, AppScope.InviteMembers))
      .map((member) => ({
        member,
        user: users.find((u) => u.userId === member.userId)
      }))
      .filter((entry) => entry.user)
      .map((entry) => ({
        label: `${entry.user!.name} (${entry.member.householdRelationship})`,
        value: entry.member.userId
      }));
  });

  /**
   * Why no inviter is offered, said as what this console read rather than as a fact about the group.
   *
   * inviterOptions() empties for two different reasons that look identical from here: nobody holds the
   * capability, or the roster's scopes arrived in a shape scopeAllows does not recognise. The algebra
   * is deliberately strict, so a namespace-qualified "group/Members.Invite" matches the bare
   * "Members.Invite" in neither direction, and this app cannot verify which spelling
   * GroupMemberResponseV1.scopes uses. Asserting the first would send an operator to grant a
   * capability somebody already holds, against a server that is answering correctly.
   */
  readonly inviterAbsenceReason = computed<string>(() => {
    const members = this.groupMembers();

    if (members.length === 0) {
      return 'This group has no members, so there is nobody for Access Management to record an invitation against.';
    }

    // One subject, one negation, composed in one place. Built as two half-sentences it read "None of
    // its 3 members not report holding Members.Invite" -- a double negative saying every member holds
    // it, on the panel that exists to explain there is nobody who does.
    const subject = members.length === 1 ? 'Its one member does not' : `None of its ${members.length} members`;
    return (
      `${subject} report holding ${AppScope.InviteMembers}, and Access Management records every ` +
      'invitation as the member who sent it, so there is nobody to send one on its behalf. Grant the ' +
      "capability to a member, or check whether the owner's role still carries it."
    );
  });

  readonly groupAccounts = computed<AccountResponseV1[]>(() => {
    const group = this.selectedGroupForMembers();
    const allAccounts = this.accounts();
    if (!group?.accountIds || group.accountIds.length === 0) return [];

    return allAccounts.filter((account) => group.accountIds!.includes(account.accountId));
  });

  readonly paymentAccountName = computed<string>(() => {
    const group = this.selectedGroupForMembers();
    const allAccounts = this.accounts();
    if (!group?.paymentAccountId) return 'Unknown';

    const account = allAccounts.find((a) => a.accountId === group.paymentAccountId);
    return account?.name || 'Unknown';
  });

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onCreateGroup(): void {
    this.dialogMode.set(DialogMode.Create);
    this.selectedGroupId.set(null);
    this.groupFormModel.set({
      paymentAccountId: '',
      name: '',
      description: '',
      groupType: GroupType.Household,
      isActive: true
    });
    this.showDialog.set(true);
  }

  onEditGroup(group: GroupResponseV1): void {
    this.dialogMode.set(DialogMode.Edit);
    this.selectedGroupId.set(group.groupId);
    this.groupFormModel.set({
      groupId: group.groupId,
      paymentAccountId: group.paymentAccountId,
      name: group.name,
      description: group.description || '',
      groupType: group.groupType,
      isActive: group.isActive
    });
    this.showDialog.set(true);
  }

  async onSaveGroup(): Promise<void> {
    if (!this.isFormValid()) {
      this.showError('Please fix validation errors');
      return;
    }

    this.saving.set(true);
    try {
      // Trimmed once, here: creating and renaming used to disagree about it, so the same typed name produced
      // a padded group on create and a clean one on rename.
      const formData = { ...this.groupFormModel(), name: this.groupFormModel().name.trim() };
      const operation =
        this.dialogMode() === DialogMode.Create
          ? this.accessManagementService.createGroup(this.toCreateRequest(formData))
          : this.accessManagementService.renameGroup(this.selectedGroupId()!, formData.name);

      await firstValueFrom(operation);
      this.showSuccess(`Group ${this.dialogMode() === DialogMode.Create ? 'created' : 'renamed'} successfully`);
      this.showDialog.set(false);
      this.groupsResource.reload();
    } catch (error) {
      // Read through refusalReason. Every way this create can be refused is a sentence the engine
      // already wrote -- the payer holds a household, the name is taken, the type is retired -- and
      // "Failed to save group" replaced all of them with something that reads as an outage.
      const reason = refusalReason(error);
      this.showError(reason ? `Failed to save the group: ${reason}` : 'Failed to save group');
      console.error('Save group error:', error);
    } finally {
      this.saving.set(false);
    }
  }

  /**
   * Turns the dialog's form into the body Access Management accepts.
   *
   * The founding Owner is the half the form does not collect and the engine will not do without: a
   * household naming no Owner is refused, because the Owner bundle is the only source of the roster
   * capabilities and a household without one can never be given them. It is not a choice either --
   * every founding member has to own the account paying for the group, so the account already names
   * the only Youtopian this create may enroll.
   *
   * There is no other type to branch on: Household is the only one the engine creates, so the payer is
   * attached on every create this method builds.
   */
  private toCreateRequest(formData: GroupFormModel): CreateGroupRequestV1 {
    const request: CreateGroupRequestV1 = {
      paymentAccountId: formData.paymentAccountId,
      name: formData.name,
      description: formData.description,
      groupType: formData.groupType,
      isActive: formData.isActive
    };

    // Household is the only creatable type, and its one founding member must be the payment account's
    // owner. A request without them is refused, so it is attached here rather than left to the caller.
    const payer = this.accounts().find((account) => account.accountId === formData.paymentAccountId);

    return payer
      ? { ...request, members: [{ userId: payer.userId, householdRelationship: HouseholdRelationship.Owner }] }
      : request;
  }

  onDeleteGroup(group: GroupResponseV1): void {
    // Households get the Soft/Hard modal (the mode-aware household teardown). Single groups keep the
    // plain confirm + generic group delete: the hard household endpoint is not type-guarded and would
    // tear down the single member's whole identity graph.
    if (group.groupType === GroupType.Household) {
      this.householdPendingDelete.set(group);
      this.showDeleteDialog.set(true);
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to delete group "${group.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteGroup(group)
    });
  }

  private async deleteGroup(group: GroupResponseV1): Promise<void> {
    this.deletingId.set(group.groupId);
    try {
      await firstValueFrom(this.accessManagementService.deleteGroup(group.groupId));
      this.showSuccess(`Group ${group.name} deleted successfully`);
      this.groupsResource.reload();
    } catch (error) {
      this.showError('Failed to delete group');
      console.error('Delete group error:', error);
    } finally {
      this.deletingId.set(null);
    }
  }

  async onConfirmDeleteHousehold(mode: DeleteMode): Promise<void> {
    const group = this.householdPendingDelete();
    if (!group) return;

    this.deletingId.set(group.groupId);
    try {
      await firstValueFrom(this.accessManagementService.deleteHousehold(group.groupId, mode));
      this.showSuccess(`Household ${group.name} deleted successfully`);
      this.showDeleteDialog.set(false);
      this.groupsResource.reload();
    } catch (error) {
      this.showError('Failed to delete household');
      console.error('Delete household error:', error);
    } finally {
      this.deletingId.set(null);
    }
  }

  async onManageMembers(group: GroupResponseV1): Promise<void> {
    this.selectedGroupForMembers.set(group);
    this.membersLoading.set(true);
    this.showMembersDialog.set(true);

    this.inviteEmail.set('');
    this.inviteHouseholdRelationship.set(HouseholdRelationship.Member);
    this.inviteRoleName.set('Member');
    this.inviteInviterUserId.set('');

    // Keyed by userId alone and cleared only on accept or reject, so closing the dialog without
    // confirming left it standing. Open another household where the same person is a member and the
    // picker showed the role staged for the first one with Apply enabled, one click from sending it to
    // the wrong household. The same staleness the default-inviter reset above exists for.
    this.stagedRoleName.set({});

    // Before the await, so nothing downstream reads the previous group's roster while this one loads.
    this.groupMembers.set([]);

    try {
      const members = await firstValueFrom(this.accessManagementService.getGroupMembers(group.groupId));
      this.groupMembers.set(members);
    } catch (error) {
      this.showError('Failed to load group members');
      console.error('Load group members error:', error);
      this.groupMembers.set([]);
    } finally {
      this.membersLoading.set(false);
    }
  }

  /**
   * Sends one invitation. A 200 does not mean the invitee was invited: the engine reports per-invitee
   * outcomes in the body, and the common refusal here is an email that already has a Youtopia account.
   * Reporting the reason verbatim is the point — staff otherwise see success and wait for an email that
   * was never sent.
   */
  async onInviteMember(): Promise<void> {
    const group = this.selectedGroupForMembers();
    const email = this.inviteEmail().trim();
    const inviterUserId = this.inviteInviterUserId();

    if (!group || !email || !inviterUserId) {
      this.showError('Enter an email address and choose who the invitation is sent by');
      return;
    }

    this.inviting.set(true);
    try {
      const batch = await firstValueFrom(
        this.invitationService.stageInvitationsForCheckout(group.groupId, {
          inviterUserId,
          invitees: [
            {
              email,
              householdRelationship: this.inviteHouseholdRelationship(),
              roleName: this.inviteRoleName(),
              shareInviterInformation: false
            }
          ]
        })
      );

      const refusal = batch.errors[0];
      if (refusal) {
        this.showError(
          refusal.alreadyPending
            ? `${refusal.email} already has a pending invitation to this group`
            : `${refusal.email} was not invited: ${refusal.reason}`
        );
        return;
      }

      // A batch with neither a refusal nor a matching success says nothing about this invitee, so
      // reporting it as staged would leave staff believing the household can now buy them a seat.
      const staged = batch.successes.find(
        (invitation) => invitation.inviteeEmail?.toLowerCase() === email.toLowerCase()
      );
      if (!staged) {
        this.showError(`The invitation to ${email} was not confirmed. Check the invitations list before retrying.`);
        return;
      }

      // Staging the same address twice succeeds and returns the first invitation, so without this the
      // second attempt reads exactly like the first and staff re-stage an address that was already done.
      if (staged.alreadyPending) {
        this.showSuccess(`${email} already had an invitation staged. It still sends once their seat is bought.`);
        this.inviteEmail.set('');
        return;
      }

      this.showSuccess(`Invitation to ${email} staged. It sends once the household buys their seat.`);
      this.inviteEmail.set('');
    } catch (error) {
      // The engine answers 403 with the missing capability in the body. An invitation carries a role,
      // and acceptance installs that role's bundle, so staging one for anything but the default role
      // takes group/Members.Manage from the INVITER this form names rather than from the operator
      // filling it in. Reporting only "failed" sends staff to retry the same pairing, when the move
      // that works is choosing an inviter who holds it.
      const reason = refusalReason(error);
      this.showError(reason ? `Failed to stage the invitation: ${reason}` : 'Failed to stage the invitation');
      console.error('Invite member error:', error);
    } finally {
      this.inviting.set(false);
    }
  }

  /** The role staged for this member, falling back to the one they hold. */
  selectedRoleName(member: UserWithRole): string {
    return this.stagedRoleName()[member.user.userId] ?? member.role.name;
  }

  hasStagedRoleChange(member: UserWithRole): boolean {
    return this.selectedRoleName(member) !== member.role.name;
  }

  onStageRole(member: UserWithRole, roleName: string): void {
    this.stagedRoleName.update((staged) => ({ ...staged, [member.user.userId]: roleName }));
  }

  /**
   * Moves a member onto the staged role. Confirmed because it moves a privilege, and the confirmation
   * describes the bundle rather than the person: what moves here is the role's scopes, and a capability
   * granted to this member individually stands on its own and is untouched. Saying they "will no longer
   * be able to" is therefore only true of a member holding no overrides.
   *
   * Three messages, because the engine makes three different changes. Arriving on Admin and leaving it
   * are the moves that reach the member: those publish the household admin granted and revoked events,
   * which Notification turns into a toast naming the promotion. A move between two roles that are
   * neither is silent, and the copy does not promise an announcement that will not come.
   */
  onApplyRole(member: UserWithRole): void {
    const group = this.selectedGroupForMembers();
    if (!group) return;

    const roleName = this.selectedRoleName(member);
    if (roleName === member.role.name) return;

    const becomingAdmin = roleName === AdminRoleName;
    const leavingAdmin = member.isHouseholdAdmin && !becomingAdmin;

    const message = becomingAdmin
      ? `Make ${member.user.name} a household admin? The Admin bundle carries ordering, meal planning and the household wallet. It does not carry inviting, and anything granted to them individually is unaffected.`
      : leavingAdmin
        ? `Move ${member.user.name} off Admin onto ${roleName}? They lose the Admin bundle: ordering, meal planning and the household wallet. Anything granted to them individually stays, so check their permissions if a capability should go with the role.`
        : `Move ${member.user.name} from ${member.role.name} to ${roleName}? They get that role's bundle in place of their current one. Anything granted or denied to them individually stays, and nothing is sent to them about this.`;

    this.confirmationService.confirm({
      message,
      header: becomingAdmin ? 'Grant Household Admin' : leavingAdmin ? 'Revoke Household Admin' : 'Change Role',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        this.changingRoleUserId.set(member.user.userId);

        try {
          await firstValueFrom(this.accessManagementService.setMemberRole(group.groupId, member.user.userId, roleName));
          this.groupMembers.set(await firstValueFrom(this.accessManagementService.getGroupMembers(group.groupId)));
          this.clearStagedRole(member.user.userId);
          this.showSuccess(
            becomingAdmin
              ? `${member.user.name} is now a household admin`
              : leavingAdmin
                ? `${member.user.name} is no longer a household admin, and now holds ${roleName}`
                : `${member.user.name} now holds ${roleName}`
          );
        } catch (error) {
          // The engine answers 400 with the reason in the body for a role it will not assign and for a
          // group that is not a household. Reporting only "failed" sends an operator to retry the same
          // move against a rule that will refuse it again.
          //
          // Read through refusalReason, which knows both of AccessManagement's refusal shapes. This
          // route's catches all go through BaseTrigger.LogExceptionAndReturnBadRequest, which answers
          // ServiceResponseV1<string> -- the reason is in `data` and there is no `error` key -- so
          // reading `.error` was always undefined and every refusal read "Failed to change the role",
          // which is exactly what naming the reason was added to stop.
          const reason = refusalReason(error);
          this.showError(reason ? `Could not change the role: ${reason}` : 'Failed to change the role');
          console.error('Set member role error:', error);
        } finally {
          this.changingRoleUserId.set(null);
        }
      },
      reject: () => this.clearStagedRole(member.user.userId)
    });
  }

  private clearStagedRole(userId: string): void {
    this.stagedRoleName.update((staged) => {
      const { [userId]: _dropped, ...rest } = staged;
      return rest;
    });
  }

  onRemoveMember(userId: string): void {
    const group = this.selectedGroupForMembers();
    if (!group) return;

    this.confirmationService.confirm({
      message: 'Are you sure you want to remove this member from the group?',
      header: 'Remove Member',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        this.membersLoading.set(true);

        try {
          await firstValueFrom(this.accessManagementService.removeMemberFromGroup(group.groupId, userId));
          this.showSuccess('Member removed successfully');

          // Reload members
          const members = await firstValueFrom(this.accessManagementService.getGroupMembers(group.groupId));
          this.groupMembers.set(members);
          this.groupsResource.reload();
        } catch (error) {
          this.showError('Failed to remove member');
          console.error('Remove member error:', error);
        } finally {
          this.membersLoading.set(false);
        }
      }
    });
  }

  // The wire value is already the label. It used to map 0/1 to Single/Household, which against a string
  // payload took the else branch every time and tagged every row, Self groups included, "Household".
  getGroupTypeLabel(groupType: GroupType): string {
    return groupType;
  }

  getStatusSeverity(isActive: boolean): 'success' | 'danger' {
    return isActive ? 'success' : 'danger';
  }

  getStatusLabel(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  protected readonly isFormValid = computed<boolean>(() => {
    const model = this.groupFormModel();
    if (!model.name.trim() || !this.groupForm.name().valid()) {
      return false;
    }
    // Only creation sends a payment account, so requiring one in Edit mode would block a rename over
    // a field the save path never reads.
    if (!this.isCreateMode()) {
      return true;
    }
    return !!(model.paymentAccountId && this.groupForm.paymentAccountId().valid());
  });

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }
}
